import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { getPageParams, PageRequest, SpringPage } from '@shared/page';
import { addHttpParams } from '@shared/utils/request-utils';
import { Observable } from 'rxjs';
import { SiteMode } from '@shared/services/hostMode.service';
import { ClusterUidAndName } from '../models/cluster';
import { ClusterConfig } from './organization-cluster.service';
import { OrganizationBase } from '@shared/services/organization.service';

@Injectable({
  providedIn: 'root',
})
export class AdminClusterService {
  constructor(private readonly http: HttpClient) {}

  getAll(): Observable<Cluster[]> {
    return this.http.get<Cluster[]>('admin/clusters', { headers: { unpaged: 'true' } });
  }

  getPage(query: ClusterQuery = null, page: PageRequest = null) {
    let params = addHttpParams(query, getPageParams(page));

    return this.http.get<SpringPage<Cluster>>('admin/clusters', { params });
  }

  create(createClusterQuery: ClusterData): Observable<Cluster> {
    return this.http.post<Cluster>('admin/clusters', createClusterQuery);
  }

  patch(uid: string, createClusterQuery: ClusterData): Observable<Cluster> {
    return this.http.patch<Cluster>(`admin/clusters/${uid}`, createClusterQuery);
  }

  delete(uid: string): Observable<void> {
    return this.http.delete<void>(`admin/clusters/${uid}`);
  }

  reprovAll(uid: string | number): Observable<void> {
    return this.http.post<void>(`admin/clusters/${uid}/reprov-all`, null);
  }

  provisionBrainParameters(uid: string) {
    return this.http.post<void>(`admin/clusters/${uid}/brain-parameters/provision`, null);
  }
}

export type Cluster = ClusterUidAndName &
  ClusterConfig & {
    type: IdAndName;
    elasticsearch: IdAndName;
    endpoint: string;
    ip: string;
    ipv6: string;
    isPublic: boolean;
    logExport: boolean;
    managed: boolean;
    opened: boolean;
    trustedIps: string[];
    twin: ClusterUidAndName;
    owner: OrganizationBase;
    cdnId: string;

    createdAt: string;
    updatedAt: string;
  };

export type ClusterData = ClusterUidAndName & {
  type: Id;
  elasticsearch: Id;
  endpoint: string;
  ip: string;
  ipv6?: string;
  isPublic: boolean;
  defaultSiteMode: SiteMode;
  managed: boolean;
  opened: boolean;
  supportsIpv6Origins: boolean;
  supportsCache: boolean;
  trustedIps: string[];
  twin?: Uid;
  owner: Code;
  cdnId?: string;
};

type IdAndName = {
  id: number;
  name: string;
};

type Id = {
  id: number;
};

type Uid = {
  uid: string;
};

type Code = {
  code: string;
};

interface ClusterQuery {
  filter?: string;
  contractIsNull?: boolean;
}
